import axios from 'axios'

const API_BASE_URL = (import.meta as any).env.VITE_API_BASE_URL ||
  (typeof window !== 'undefined'
    ? window.location.hostname === 'localhost'
      ? 'http://localhost:2000'
      : window.location.hostname.includes('stg-')
        ? 'https://stg-retri-b90a7a43a76f.herokuapp.com'
        : 'https://retriapp.herokuapp.com'
    : 'http://localhost:2000')

const PLATFORM_PREFIX = '/api/v2/platform'

const platformApi = axios.create({
  baseURL: `${API_BASE_URL}${PLATFORM_PREFIX}`,
  headers: { 'Content-Type': 'application/json' },
})

platformApi.interceptors.request.use((config) => {
  const token = localStorage.getItem('auth_token')
  if (token) config.headers.Authorization = `Bearer ${token}`
  return config
})

platformApi.interceptors.response.use(
  (r) => r,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('auth_token')
      localStorage.removeItem('operator')
      window.location.href = '/login'
    }
    return Promise.reject(error)
  }
)

export type ExpenseType =
  | 'FUEL' | 'MAINTENANCE' | 'PARTS' | 'OPERATOR_SALARY'
  | 'INSURANCE' | 'TRANSPORT' | 'OTHER'

export interface MachineryExpense {
  id: number
  machineryId: number | null
  subcontractedMachineryId: number | null
  assetId: number | null
  companyId: number
  type: ExpenseType
  expenseDate: string
  receiptNumber: string | null
  quantity: string
  unitPrice: string
  totalAmount: string
  photoUrl: string | null
  notes: string | null
  createdBy: number | null
  createdAt: string
  updatedAt: string
  deletedAt: string | null
  machineryPlate?: string | null
  companyName?: string | null
}

export interface CreateExpensePayload {
  machineryId?: number | null
  subcontractedMachineryId?: number | null
  assetId?: number | null
  companyId: number
  type: ExpenseType
  expenseDate: string
  receiptNumber?: string | null
  quantity: number
  unitPrice: number
  totalAmount: number
  photoUrl?: string | null
  notes?: string | null
}

export interface ListExpensesQuery {
  machineryId?: number
  subcontractedMachineryId?: number
  assetId?: number
  dealId?: number
  companyId?: number
  type?: ExpenseType
  from?: string
  to?: string
}

export const machineryExpensesService = {
  list: async (q: ListExpensesQuery = {}): Promise<MachineryExpense[]> => {
    const { data } = await platformApi.get('/machinery-expenses', { params: q })
    return data
  },
  findOne: async (id: number): Promise<MachineryExpense> => {
    const { data } = await platformApi.get(`/machinery-expenses/${id}`)
    return data
  },
  create: async (payload: CreateExpensePayload): Promise<MachineryExpense> => {
    const { data } = await platformApi.post('/machinery-expenses', payload)
    return data
  },
  update: async (id: number, payload: Partial<CreateExpensePayload>): Promise<MachineryExpense> => {
    const { data } = await platformApi.patch(`/machinery-expenses/${id}`, payload)
    return data
  },
  softDelete: async (id: number): Promise<MachineryExpense> => {
    const { data } = await platformApi.delete(`/machinery-expenses/${id}`, { params: { mode: 'soft' } })
    return data
  },
  getLastFuelUnitPrice: async (params: {
    machineryId?: number | null
    subcontractedMachineryId?: number | null
    companyId?: number | null
  }): Promise<number | null> => {
    const qs: Record<string, number> = {}
    if (params.machineryId) qs.machineryId = params.machineryId
    if (params.subcontractedMachineryId) qs.subcontractedMachineryId = params.subcontractedMachineryId
    if (params.companyId) qs.companyId = params.companyId
    const { data } = await platformApi.get('/machinery-expenses/last-fuel-price', { params: qs })
    return data?.unitPrice ?? null
  },
  uploadPhoto: async (file: File): Promise<string> => {
    const fd = new FormData()
    fd.append('file', file)
    const { data } = await platformApi.post('/upload', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    return data.location
  },
}
